import { useEffect, useState } from 'react';

import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import cn from 'classnames';

import CloseIcon from '@publicImages/svg/quick-find-coaching-close.svg?static';
import TrashIcon from '@publicImages/svg/trash.svg?static';

import HtmlContent from '@/components/elements/HtmlContent';
import Tooltip from '@/components/Tooltip';
import UIResource from '@/components/UIResource';
import { resetFilters } from '@/ducks/filters/setters';
import { type FiltersKey } from '@/ducks/filters/types';
import { getQFSearchParams } from '@/features/quickFindFilters/helpers';
import { useQFActiveLink } from '@/features/quickFindFilters/hooks';
import { getSessionStorageValue, setSessionStorageValue } from '@/helpers/util';

import styles from './QuickFindFilterChip.module.scss';

const QuickFindFilterChip = () => {
  const [isCoachingMode, setIsCoachingMode] = useState(false);

  const activeQFLink = useQFActiveLink();

  useEffect(() => {
    if (activeQFLink && !getSessionStorageValue('isQuickFindCoachingUsed')) {
      setSessionStorageValue('isQuickFindCoachingUsed', true);
      setIsCoachingMode(true);
    }
  }, [activeQFLink]);

  if (!activeQFLink) {
    return null;
  }

  const onClearHandler = () => {
    if (activeQFLink) {
      const linkParams = getQFSearchParams(activeQFLink);
      const keys = [...linkParams.keys()];
      resetFilters(keys as FiltersKey[]);
    }
  };

  return (
    <Popover open={isCoachingMode}>
      <PopoverTrigger asChild>
        <div className={cn(styles.root, { [styles.coaching as string]: isCoachingMode })}>
          <div className={styles.wrapper}>
            {activeQFLink.description ? (
              <Tooltip
                opener={activeQFLink.linkTitle}
                openerClassName={styles.text}
                popupClassName={styles.tooltipContent}
                portal
                side="bottom"
              >
                <HtmlContent content={activeQFLink.description} />
              </Tooltip>
            ) : (
              <span className={styles.text}>{activeQFLink.linkTitle}</span>
            )}

            <button aria-label="Remove quick find filter" className={styles.button} onClick={onClearHandler}>
              <TrashIcon className={styles.icon} />
              <UIResource id="QuickFind.clear.CTA" />
            </button>
          </div>
        </div>
      </PopoverTrigger>

      <PopoverContent className={styles.coachingTooltip}>
        <span className={styles.coachingTooltipText}>
          <UIResource id="QuickFind.tooltip" />
        </span>

        <PopoverClose className={styles.coachingTooltipClose} onClick={() => setIsCoachingMode(false)}>
          <CloseIcon className={styles.coachingTooltipCloseIcon} />
          <div className="sr-only">Close</div>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  );
};

export default QuickFindFilterChip;
